import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import * as gql from 'gql-query-builder';
import {EnumUtils} from '@shared/util/general/enum-utils';
import {BaseService} from '@shared/services/api/base-service';
import {PeriodUtilService} from '@shared/services/util/period-util.service';
import {Equipment, EquipmentId, Meter, MeterProvider, MeterStream, MeterStreamHistory, MeterStreamSourceFavouriteConfig, MeterType, MeterUtilityType, Site, VirtualMeterCalculationType} from '@app/graphql-typings/graphql';
import {I18nError} from '@app/custom-error-handler';
import {PagedModel} from '@shared/api/model/paged-model';
import {HttpUtilityService} from '@shared/services/api/http-utility-service';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import {APIResponse, DeleteResponseData} from '@app/layout/admin/services/api/api.interface';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import moment, {Moment} from 'moment-timezone';
import {CustomerId} from "@shared/services/api/user-entity";
import {ClassTransformer, Transform, Type} from "class-transformer";
import {SiteId} from "@shared/services/api/access-sites.interface";
import {MeterId, MeterProviderId, StreamId} from "@shared/services/api/meter-provider.interface";

export class MetersSearchTerm {
  nmi_id?: number;
  nmi_ids?: number[];
  site_id?: number;
  site_ids?: number[];
  nmi_number?: string;
  nmi_numbers?: string[];
  provider_id?: number;
  available?: boolean;
  utility_type?: string;
  start_index?: number;
  limit?: number;
}

export enum MeterConsumptionType {USAGE = 'Usage', COST = 'Cost', EMISSIONS = 'Emissions', DEMAND = 'Demand', NORMALISATION = 'Normalisation'}

export enum DataInterval {PT5M = '5 min', PT15M = '15 min', PT30M = '30 min', PT1H = '1 hour', P1D = '1 day', P1W = '1 week', P1M = '1 month'}

export namespace DataInterval {
  export function lessThan(interval: DataInterval, other: DataInterval): boolean {
    return EnumUtils.lessThan(DataInterval, interval, other);
  }

  export function lessThanOrEqual(interval: DataInterval, other: DataInterval): boolean {
    return EnumUtils.lessThanOrEqual(DataInterval, interval, other);
  }

  export function toIsoString(interval: DataInterval): string | null {
    if (interval != null) {
      const enumKey = EnumUtils.getEnumKeyByValue(DataInterval, interval);
      if (enumKey != null) {
        return enumKey;
      } else {
        throw new Error('Unable to convert DataInterval ' + interval + ' to ISO 8601 Duration String');
      }
    } else {
      return null;
    }
  }

  export function get(interval: string): DataInterval | null {
    return interval
      // Backend and momentjs will always return P7D instead of P1W as iso string, therefore here we convert P7D to cater for data from either backend or momentjs
      ? (DataInterval[interval === 'P7D' ? 'P1W' : interval] ?? null)
      : null;
  }

  export function parse(value: string): DataInterval | null {
    const key = EnumUtils.getEnumKeyByValue(DataInterval, value);
    return key ? <DataInterval>DataInterval[key] : null;
  }
}

export interface ReportableStreamData {
  stream: MeterStream;
  type: 'parent' | 'child';
}

export interface SourceFavValidationResponse {
  fav_id: number;
  source_favourite_field: SourceFavouriteConfigField;
  valid: boolean;
  message: string;
}

export enum SourceFavouriteConfigField {
  ACTIVE_IMPORT_FAVOURITE = 'active_import_fav_id',
  ACTIVE_EXPORT_FAVOURITE = 'active_export_fav_id',
  REACTIVE_IMPORT_FAVOURITE = 'reactive_import_fav_id',
  REACTIVE_EXPORT_FAVOURITE = 'reactive_export_fav_id'
}


class MeterStreamImpl implements MeterStream {
  base_temperature?: number;
  base_temperature_heating?: number;
  description?: string;
  display_name?: string;
  equipment: Equipment;
  equipment_id: EquipmentId;
  fill_gaps?: boolean;
  history_available?: boolean;

  meter_stream_source_favourite_config?: MeterStreamSourceFavouriteConfig;

  @Type(() => MeterImpl)
  nmi: Meter;

  nmi_id: MeterId;
  nmi_number: string;
  norm_intercept?: number;
  norm_occupancy_co?: number;
  norm_slope?: number;
  norm_slope_heating?: number;
  reportable: boolean;
  stream_history?: MeterStreamHistory[];
  stream_id: StreamId;

  @Type(() => MeterImpl)
  sub_meters?: Meter[];

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at: Moment;

  get displayName(): string {
    return this.display_name || this.stream_id;
  }
}


class MeterImpl implements Meter {
  // todo: coerce into moment duration
  // currently used as string
  collection_interval?: string;

  customer_id?: CustomerId;

  // todo: coerce into moment duration
  // currently used as string
  data_interval?: string;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.duration(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: moment.Duration | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  demand_peak_calculation_interval?: moment.Duration;

  display_name?: string;
  history_available?: boolean;

  meter_type: MeterType;
  nmi_id: number;
  nmi_number: string;

  @Type(() => MeterStreamImpl)
  nmi_streams?: MeterStream[];
  parent_nmi_id?: number;

  @Type(() => MeterStreamImpl)
  parent_stream?: MeterStream;

  parent_stream_id?: string;
  peak_demand_max_target?: number;
  provider?: MeterProvider;
  provider_id: number;
  site?: Site;
  site_id?: SiteId;
  timezone?: string;
  utility_type: MeterUtilityType;
  virtual_meter_calculation_entities?: number[];
  virtual_meter_calculation_type?: VirtualMeterCalculationType;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at?: Moment;

  get displayName(): string {
    return this.display_name || this.nmi_number;
  }
}


@Injectable({
  providedIn: 'root'
})
export class MetersService extends BaseService {

  private headers: HttpHeaders = this.getHeaders();
  private readonly ID_SEPARATOR = ',';
  private readonly FETCH_SIZE = 1000;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private periodUtil: PeriodUtilService,
              private classTransformer: ClassTransformer) {
    super();
  }

  getAllMeters(searchTerm?: MetersSearchTerm, checkHistory?: boolean): Observable<Meter[]> {
    const searchPageWrapper = (pageInfo: PageInfo): Observable<PagedModel<Meter[]>> => {
      return this.getMeters({...searchTerm, start_index: pageInfo.first, limit: pageInfo.rows}, checkHistory);
    };
    return this.httpUtilityService.pageUntilComplete(searchPageWrapper, {first: 0, rows: this.FETCH_SIZE});
  }

  getMeters(searchTerm?: MetersSearchTerm, checkHistory?: boolean): Observable<PagedModel<Meter[]>> {
    const historyAvailableField = (checkHistory ? ['history_available'] : []);
    const operation = 'nmi_numbers';
    const q = gql.query({
      operation,
      fields: [
        'nmi_id', 'nmi_number', 'display_name', 'data_interval', 'meter_type', 'utility_type', 'timezone', 'parent_nmi_id', 'parent_stream_id',
        'peak_demand_max_target', 'site_id', 'provider_id', 'virtual_meter_calculation_type', 'virtual_meter_calculation_entities',
        {
          nmi_streams: [
            'equipment_id', 'nmi_id', 'nmi_number', 'stream_id', 'display_name', 'description', 'base_temperature', 'norm_slope', 'norm_intercept',
            'norm_occupancy_co', 'base_temperature_heating', 'norm_slope_heating', 'reportable', 'fill_gaps',
            ...historyAvailableField,
            {
              meter_stream_source_favourite_config: [
                'stream_id', 'nmi_id', 'active_max_value', 'reactive_max_value', 'active_multiplier', 'reactive_multiplier',
                'active_import_fav_id', 'active_export_fav_id', 'reactive_import_fav_id', 'reactive_export_fav_id',
                'active_interval_usage_limit', 'reactive_interval_usage_limit'
              ]
            }
          ]
        },
        {site: ['site_id', 'site_name']},
        {provider: ['provider_id', 'provider_name', 'timezone']},
      ],
      variables: {
        nmi_id: {type: 'Int'},
        nmi_ids: {type: 'Int', list: true},
        site_id: {type: 'Int'},
        site_ids: { type: 'Int', list: true },
        provider_id: {type: 'Int'},
        nmi_number: {type: 'String'},
        nmi_numbers: {type: 'String', list: true},
        available: {type: 'Boolean'},
        utility_type: {type: 'MeterUtilityTypeEnum'},
        start_index: {type: 'Int'},
        limit: {type: 'Int'}
      }
    });

    const variables = {
      ...q.variables,
      ...searchTerm
    };
    return this.httpUtilityService.graphQL<Meter[]>(q, operation, variables).pipe(map(metersPage => {
      metersPage.data = metersPage.data.map(meter => {
        const meterImpl = this.classTransformer.plainToInstance(MeterImpl, meter);
        if (checkHistory) {
          meterImpl.history_available = false;
        }
        meterImpl.nmi_streams?.forEach(streamImpl => {
          streamImpl.nmi = meterImpl;
          if (checkHistory) {
            meterImpl.history_available ||= streamImpl.history_available;
          }
        });
        return meterImpl;
      });
      return metersPage;
    }));
  }

  getMeter(nmiId: number, checkHistory?: boolean): Observable<Meter> {
    return this.getMeters({nmi_id: nmiId}, checkHistory)
      .pipe(map(({count: _count, data: meters}: PagedModel<Meter[]>) => meters && meters.length ? meters[0] : null));
  }

  toDataInterval(str: string): DataInterval | null {
    const interval = this.periodUtil.toIsoString(str);
    return DataInterval.get(interval);
  }

  upsertMeter(meter: Meter): Observable<Meter> {
    return this.http.post<Meter>(this.getGateway() + `/providers/${meter.provider_id}/nmi`, this.toApiReady(meter), {headers: this.headers})
      .pipe(map(response => {
        const meter = this.httpUtilityService.extractResponseData(response, 'nmi_number');
        return this.classTransformer.plainToInstance(MeterImpl, meter);
      }));
  }

  upsertMeterStreams(providerId: number, streams: MeterStream[]): Observable<MeterStream[]> {
    if (!streams || !streams.length) {
      return of([]);
    }

    return this.http.post<MeterStream[]>(this.getGateway() + `/providers/${providerId}/nmi/streams`, this.streamsToApiReady(streams), {headers: this.headers})
      .pipe(map(response => {
        const streams: MeterStream[] = this.httpUtilityService.extractResponseData(response, 'nmi_streams');
        return this.classTransformer.plainToInstance(MeterStreamImpl, streams);
      }));
  }

  upsertMeterStreamsBySiteId(siteId: number, streams: MeterStream[]): Observable<MeterStream[]> {
    if (!streams || !streams.length) {
      return of([]);
    }

    return this.http.post<MeterStream[]>(this.getGateway() + `/sites/${siteId}/nmi/streams`, this.streamsToApiReady(streams), {headers: this.headers})
      .pipe(map(response => {
        const streams: MeterStream[] = this.httpUtilityService.extractResponseData(response, 'nmi_streams');
        return this.classTransformer.plainToInstance(MeterStreamImpl, streams);
      }));
  }

  deleteMeter(providerId: MeterProviderId, nmiId: MeterId): Observable<boolean> {
    return this.http.delete(this.getGateway() + `/providers/${providerId}/nmi/${nmiId}`, {headers: this.headers})
      .pipe(map(response => (<DeleteResponseData>this.httpUtilityService.extractResponseData(response, 'nmi_number')).deleted));
  }

  deleteMeterStreams(providerId: MeterProviderId, nmiId: MeterId, streamIds: StreamId[]): Observable<Map<StreamId, boolean> | null> {
    if (!streamIds?.length) {
      return of(null);
    }

    const deleteObs: Observable<[StreamId, boolean]>[] = streamIds.map(streamId =>
      this.http.delete(this.getGateway() + `/providers/${providerId}/nmi/${nmiId}/streams/${streamId}`, {headers: this.headers}).pipe(
        map(response => [streamId, (<DeleteResponseData>this.httpUtilityService.extractResponseData(response, 'nmi_stream')).deleted]))
    );
    return forkJoin(deleteObs).pipe(map(streamDeleteResults => new Map(streamDeleteResults)));
  }

  isValidNmi(nmi: string): boolean {
    return nmi && (nmi.length === 10 || nmi.length === 11);
  }

  /**
   * Find and return a reportable stream that is an ancestor or a descendant of the provided stream, or null if no reportable stream is found.
   *
   * *NOTE*: The provided stream is *NOT* checked, only the stream's ancestors and descendants.
   *
   * @param stream whose ancestors and descendants will be searched for a reportable stream, should be null for a stream that is only being
   * created, and has not been persisted yet. In this case pass in the parentStream parameter
   * @param parentStream? the stream that should be used as the parent stream of the provided stream. Should be provided if the stream is being
   * created and has not been persisted yet, or is being edited so that it's parent could have changed, but the change has not been
   * persisted yet
   * @param metersById a Map of meters keyed by their id
   * @throws If metersById does not contain a meter or stream in the ancestor or decendant hierarchy of stream
   */
  getReportableStreamInHierarchy(stream: MeterStream, metersById: Map<number, Meter>, parentStream?: MeterStream): ReportableStreamData {
    // NOTE: If the stream's nmi_id is null, it means that the stream's meter is being created. Therefore, it cannot have descendant meters yet,
    // and it's parent, if any, needs to be explicitly passed in via the parentStream parameter
    const reportableParentStream =  parentStream != null
      ? (parentStream.reportable ? parentStream : this.getReportableAncestorStream(parentStream, metersById))
      : (stream != null && stream.nmi_id != null ? this.getReportableAncestorStream(stream, metersById) : null);
    if (reportableParentStream) {
      return {stream: reportableParentStream, type: 'parent'};
    }

    if (stream == null || stream.nmi_id == null) {
      return null;
    } else {
      const reportableChildStream = this.getReportableDescendantStream(stream, metersById);
      return reportableChildStream ? {stream: reportableChildStream, type: 'child'} : null;
    }
  }

  /**
   * Find and return a reportable stream that is an ancestor of the provided stream, or null if no reportable stream is found.
   *
   * *NOTE*: The provided stream is *NOT* checked, only the stream's ancestors.
   *
   * @param stream whose ancestors will be searched for a reportable stream
   * @param metersById a Map of meters keyed by their id
   * @throws If metersById does not contain a meter or stream in the ancestor hierarchy of stream
   */
  getReportableAncestorStream(stream: MeterStream, metersById: Map<number, Meter>): MeterStream | null {
    // check current stream
    const meterForStream: Meter = metersById.get(stream.nmi_id);
    if (!meterForStream) {
      throw new I18nError('Cannot find Meter with id', {id: stream.nmi_id});
    }
    // check if parent stream
    if (meterForStream.parent_nmi_id == null) {
      return null;
    } else {
      const parentMeter: Meter = metersById.get(meterForStream.parent_nmi_id);
      if (!parentMeter) {
        throw new I18nError('Cannot find Meter with id', {id: meterForStream.parent_nmi_id});
      }
      const ps: MeterStream = parentMeter.nmi_streams.find(s => s.stream_id === meterForStream.parent_stream_id);
      if (!ps) {
        throw new I18nError('Cannot find Stream with id', {id: meterForStream.parent_stream_id});
      }

      if (ps.reportable) {
        return ps;
      }

      return this.getReportableAncestorStream(ps, metersById);
    }
  }

  /**
   * Find a return a reportable stream that is a descendant of the provided stream, or null if none found.
   *
   * *NOTE*: The provided stream is *NOT* checked, only the streams of its descendants.
   *
   * @param stream whose descendants should be searched for a reportable stream
   * @param metersById a Map of meters keyed by their id
   * @throws If metersById does not contain a meter or stream in the descendant hierarchy of stream
   */
  getReportableDescendantStream(stream: MeterStream, metersById: Map<number, Meter>): MeterStream | null {
    return this.getChildReportableStreamProcess(stream, this.buildChildStreamsByParentStreamIdMap(metersById));
  }

  private getFullStreamId(nmiId: number, streamId?: string): string {
    return nmiId + (streamId != null ? this.ID_SEPARATOR + streamId : '');
  }

  private buildChildStreamsByParentStreamIdMap(metersById: Map<number, Meter>): Map<string, MeterStream[]> {
    const streamsByParentId: Map<string, MeterStream[]> = new Map<string, MeterStream[]>();
    metersById.forEach((meter: Meter) => {
      if (meter.parent_nmi_id && meter.parent_stream_id) {
        const id = this.getFullStreamId(meter.parent_nmi_id, meter.parent_stream_id);
        const streams = streamsByParentId.get(id);
        if (streams) {
          streams.push(...meter.nmi_streams);
        } else {
          streamsByParentId.set(id, [...meter.nmi_streams]);
        }
      }
    });
    return streamsByParentId;
  }

  private getChildReportableStreamProcess(stream: MeterStream, childStreamsByParentStreamId: Map<string, MeterStream[]>): MeterStream | null {
    // check child streams
    const id = this.getFullStreamId(stream.nmi_id, stream.stream_id);
    const childStreams: MeterStream[] = childStreamsByParentStreamId.has(id) ? childStreamsByParentStreamId.get(id) : [];
    for (let i = 0; i < childStreams.length; i++) {
      const childStream = childStreams[i];
      if (childStream.reportable) {
        return childStream;
      } else {
        const reportableStream = this.getChildReportableStreamProcess(childStreams[i], childStreamsByParentStreamId);
        if (reportableStream != null) {
          return reportableStream;
        }
      }
    }
    return null;
  }

  private toApiReady(meter: Meter): Meter {
    const flatMeter: Meter = Object.assign({}, meter);
    delete flatMeter.site;
    delete flatMeter.provider;
    delete flatMeter.nmi_streams;
    return flatMeter;
  }

  private streamsToApiReady(streams: MeterStream[]): MeterStream[] {
    return streams.map((stream: MeterStream) => {
      const flatStream: MeterStream = Object.assign({}, stream);
      delete flatStream.meter_stream_source_favourite_config;
      delete flatStream.stream_history;
      delete flatStream.sub_meters;
      delete flatStream.nmi;
      return flatStream;
    });
  }

  upsertMeterStreamSourceFavouriteConfig(providerId: number, configs: MeterStreamSourceFavouriteConfig[]): Observable<MeterStreamSourceFavouriteConfig[]> {
    if (!configs || !configs.length) {
      return of([]);
    }

    if (configs.some(c => c.nmi_id !== configs[0].nmi_id)) {
      throw new Error('All configs must have same NMI id ' + configs[0].nmi_id);
    }

    return this.http.post<MeterStreamSourceFavouriteConfig>(this.getGateway()
      + `/providers/${providerId}/nmi/${configs[0].nmi_id}/streams/source-favourite-config`, configs, {headers: this.headers})
      .pipe(map(response => this.httpUtilityService.extractResponseData(response, 'meter_stream_source_favourite_configs')));
  }

  validateMeterStreamSourceFavouriteConfig(config: MeterStreamSourceFavouriteConfig): Observable<SourceFavValidationResponse[]> {
    return this.http.post(this.getGateway() + `/meters/source-favourite-config/validate`, config, {headers: this.headers})
      .pipe(map((response: APIResponse<SourceFavValidationResponse>) => this.httpUtilityService.extractResponseData(response, 'validation_result')));
  }

  deleteMeterStreamSourceFavouriteConfigs(providerId: number, nmiId: number, streamIds: string[]): Observable<MeterStreamSourceFavouriteConfig[]> {
    if (!streamIds || !streamIds.length) {
      return of([]);
    }

    const deleteObs: Observable<MeterStreamSourceFavouriteConfig>[] = [];
    streamIds.forEach(streamId => deleteObs.push(
      this.http.delete(this.getGateway() + `/providers/${providerId}/nmi/${nmiId}/streams/${streamId}/source-favourite-config`, {headers: this.headers})
        .pipe(map(response => this.httpUtilityService.extractResponseData(response, 'meter_stream_source_favourite_config')))
    ));
    return forkJoin(deleteObs);
  }

  /**
   * Get all child streams that have a favourite source config - the provided stream is checked
   */
  getChildStreamsWithConfigs(stream: MeterStream, metersById: Map<number, Meter>): MeterStream[] {
    return this.getChildStreamsWithConfigsProcess(stream, this.buildChildStreamsByParentStreamIdMap(metersById));
  }

  private getChildStreamsWithConfigsProcess(stream: MeterStream, childStreamsByParentStreamId: Map<string, MeterStream[]>): MeterStream[] {
    const id = this.getFullStreamId(stream.nmi_id, stream.stream_id);
    const configStreams: MeterStream[] = [];
    if (stream.meter_stream_source_favourite_config) {
      configStreams.push(stream);
    }

    if (childStreamsByParentStreamId.has(id)) {
      const childStreams: MeterStream[] = childStreamsByParentStreamId.get(id);
      for (let i = 0; i < childStreams.length; i++) {
        configStreams.push(...this.getChildStreamsWithConfigsProcess(childStreams[i], childStreamsByParentStreamId));
      }
    }
    return configStreams;
  }

  getDescendantMeters(meter: Meter, metersById: Map<number, Meter>): Meter[] {
    const meters = new Map<number, Meter>();
    (meter.nmi_streams || []).forEach(stream => {
      const mIds = this.getDescendantMetersProcess(stream, this.buildChildStreamsByParentStreamIdMap(metersById));
      (mIds || []).forEach(meterId => {
        if (!meters.has(meterId)) {
          const meter = metersById.get(meterId);
          if (meter) {
            meters.set(meter.nmi_id, meter);
          } else {
            throw new Error('Cannot find meter with id ' + meterId);
          }
        }
      });
    });
    return Array.from(meters.values());
  }

  private getDescendantMetersProcess(stream: MeterStream, childStreamsByParentStreamId: Map<string, MeterStream[]>): Set<number> {
    const meterIds = new Set<number>();
    if (stream) {
      meterIds.add(stream.nmi_id);
      const id = this.getFullStreamId(stream.nmi_id, stream.stream_id);
      const childStreams: MeterStream[] = childStreamsByParentStreamId.get(id);
      if (childStreams?.length) {
        childStreams.forEach(childStream => {
          const childMeterIds = this.getDescendantMetersProcess(childStream, childStreamsByParentStreamId);
          (childMeterIds || []).forEach(meterId => meterIds.add(meterId));
        });
      }
    }
    return meterIds;
  }

  getSiblingMeters(currentMeter: Meter, parentMeter: Meter, meterMap: Map<number, Meter>): Meter[] {
    if (meterMap?.size) {
      const childStreamsByParentStreamIdMap: Map<string, MeterStream[]> = this.buildChildStreamsByParentStreamIdMap(meterMap);
      const siblingMeterMap = new Map<number, Meter>();
      (parentMeter?.nmi_streams || []).forEach(stream => {
        const streamId = this.getFullStreamId(parentMeter.nmi_id, stream.stream_id);
        const streams: MeterStream[] = childStreamsByParentStreamIdMap.get(streamId);
        (streams || []).forEach(stream => {
          if (!currentMeter || (stream.nmi_id !== currentMeter.nmi_id)) {
            if (!siblingMeterMap.has(stream.nmi_id)) {
              siblingMeterMap.set(stream.nmi_id, meterMap.get(stream.nmi_id));
            }
          }
        });
      });
      return Array.from(siblingMeterMap.values());
    } else {
      return [];
    }
  }

  getChildMeters(stream: MeterStream, metersById: Map<number, Meter>): Meter[] {
    const childStreamsByParentStreamId: Map<string, MeterStream[]> = this.buildChildStreamsByParentStreamIdMap(metersById);
    const id = this.getFullStreamId(stream.nmi_id, stream.stream_id);
    const streams = childStreamsByParentStreamId.get(id) || [];
    const metersMap: Map<number, Meter> = streams.reduce((acc: Map<number, Meter>, currentStream: MeterStream) => {
      if (!acc.has(currentStream.nmi_id)) {
        acc.set(currentStream.nmi_id, metersById.get(currentStream.nmi_id));
      }
      return acc;
    }, new Map<number, Meter>());
    return Array.from(metersMap.values());
  }

  public getQuotedNmiNumber(meter: Meter): string {
    return '"' + meter.nmi_number + '"';
  }

  public doesMeterHaveHistory(meter: Meter): boolean {
    if (meter && meter.history_available == null) {
      throw new Error('Meter being checked has no `history_available` property');
    }
    return meter?.history_available;
  }

  public doesStreamHaveHistory(stream: MeterStream): boolean {
    if (stream && stream.history_available == null) {
      throw new Error('Stream being checked has no `history_available` property');
    }
    return stream.history_available;
  }

  public areMetersSiblings(meter1: Meter, meter2: Meter): boolean {
    return meter1
      && meter2
      && meter1.nmi_id !== meter2.nmi_id
      && meter1.parent_nmi_id === meter2.parent_nmi_id
      && meter1.parent_stream_id === meter2.parent_stream_id;
  }

  public getSourceFavouriteConfigs(searchCriteria: {fav_id?: number, fav_ids?: number[]}): Observable<MeterStreamSourceFavouriteConfig[]> {
    if (ObjectUtils.isEmpty(searchCriteria)) {
      throw new Error('searchCriteria cannot be empty.');
    }
    const options = {
      headers: this.getHeaders(),
      params: {
        ...searchCriteria.fav_id && {fav_id: searchCriteria.fav_id},
        ...searchCriteria.fav_ids?.length && {fav_ids: searchCriteria.fav_ids},
      }
    };
    return this.http.get<APIResponse<MeterStreamSourceFavouriteConfig[]>>(this.getGateway() + '/meters/source-favourite-configs', options).pipe(map(response => {
      if (response[this.DATA_PREFIX]) {
        return response[this.DATA_PREFIX]['meter_stream_source_favourite_configs'];
      } else {
        throw new Error('Error fetching Source Favourite Config: ' + JSON.stringify(response[this.ERRORS_PREFIX]));
      }
    }));
  }

  private getRelatedMeters(virtualMeter: Meter, metersById: Map<number, Meter>): Meter[] {
    const relatedMeters = [];
    const parentMeter = metersById.get(virtualMeter.parent_nmi_id);
    const virtualMeterCalculationType = virtualMeter.virtual_meter_calculation_type;

    if (virtualMeterCalculationType !== VirtualMeterCalculationType.SELECTED_CHILDREN_SUMMED) {
      if (parentMeter == null) {
        throw new Error('Virtual meter with calculation type: ' + virtualMeterCalculationType + ' must have parent meter configured.');
      }
      relatedMeters.push(parentMeter);
      if (virtualMeterCalculationType === VirtualMeterCalculationType.ALL_SIBLINGS_SUBTRACTED) {
        relatedMeters.push(...this.getSiblingMeters(virtualMeter, parentMeter, metersById));
      }
    }

    virtualMeter.virtual_meter_calculation_entities?.forEach(meterId => relatedMeters.push(metersById.get(meterId)));

    return relatedMeters;
  }

  public calculateVirtualMeterStreamInterval(virtualMeter: Meter,
                                             metersById: Map<number, Meter>): moment.Duration {
    let interval = virtualMeter.data_interval;
    if (interval == null) {
       const relatedMeters = this.getRelatedMeters(virtualMeter, metersById);
       return this.periodUtil.sparsestDuration(
         relatedMeters.map(meter => this.periodUtil.toDuration(meter.data_interval)));
    }
    return this.periodUtil.toDuration(interval);
  }

}
